import React, { useState, useEffect, useContext } from 'react';
import { Route, Routes, useLocation, useNavigate, Navigate, useParams, useSearchParams } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/index.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { UserProvider, UserContext } from './contexts/UserContext.js';
import { CategoryProvider, CategoryContext } from './contexts/CategoryContext';
import { CityProvider } from './contexts/CityContext';
import { ThemeProvider } from './ThemeContext';
import { PageTitleProvider } from './contexts/PageTitleContext';
import VerifyUser from './pages/VerifyUser';
import VerifyPassword from './pages/VerifyPassword';
import RequestPasswordReset from './pages/RequestPasswordReset';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import ChangePassword from './pages/ChangePassword';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import About from './pages/AboutUs';
import Contact from './pages/ContactUs';
import Support from './pages/Support';
import { ActivityProvider } from './contexts/ActivityContext';
import Likes from './pages/ProductsLiked';
import Footer from './components/Footer.js';
import WalletView from './pages/WalletView';
import PaymentTopup from './pages/PaymentTopup';
import Register from './pages/Register';
import CompleteProfile from './pages/CompleteProfile';
import UserNotifications from './pages/UserNotifications';
import ProductsUser from './pages/ProductsUser';
import ProductsAll from './pages/ProductsAll';
import ProductsProvince from './pages/ProductsProvince';
import CategoryProductsPage from './components/CategoryProductsPage';
import PromoteProduct from './pages/PromoteProduct';
import BoostProduct from './pages/BoostProduct';
import ErrorBoundary from './components/ErrorBoundary';
import AddPost from './pages/AddPost';
import ProductDetails from './pages/ProductDetails';
import Indox from './pages/Inbox.js';
import NotFound from './pages/NotFound';
import ChatRoom from './pages/ChatRoom';
import UserCorner from './pages/UserCorner';
import BlockedUsers from './pages/BlockedUsers';
import ForgotPassword from './pages/forgotPassword.js';
import SearchResults from './pages/SearchResults';
import SetPassword from './pages/SetPassword.js';
import LoadingSpinner from './components/LoadingSpinner';
import SmartAppBanner from './components/SmartAppBanner';
import TermsConditions from './pages/TermsConditions';
import ServerDown from './pages/ServerDown';
import ProtectedRoute from './components/ProtectedRoute';
import UserConnections from './pages/UserConnections';
import DiscoverUsers from './pages/DiscoverUsers';
import SEOHead, { getPageSEO } from './components/SEOHead';
import { ReduxProvider } from './reduxSetup';
import DeleteAccount from './pages/DeleteAccount';
import DeleteAccountVerification from './pages/DeleteAccountVerification';
import CommunicationPage from './pages/CommunicationPage';
import MessageList from './pages/MessageList';
import MessageDetail from './pages/MessageDetail';
import ProductDeepLink from './pages/ProductDeepLink';
import UserDeepLink from './pages/UserDeepLink';
import WaitingList from './pages/WaitingList';
import WennzeBusiness from './pages/WennzeBusiness';

const UserCornerStatistics = React.lazy(() => import('./components/UserCornerStatistics'));

// Redirect component for legacy /userconnection route
const LegacyUserConnectionRedirect = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const type = tab === 'following' ? 'following' : 'followers';
  return <Navigate to={`/users/${userId}/${type}`} replace />;
};

const AppContent = () => {
  const { user, isLoading: userLoading, fetchUser, setUser, redirectToLogin } = useContext(UserContext);
  const [showLogin, setShowLogin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // const [loading, setLoading] = useState(false); // Currently unused
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!user);
  // const [isBackendDown, setIsBackendDown] = useState(false); // Currently unused
  const { isServerDown } = useContext(CategoryContext);
  const [forceStopLoading, setForceStopLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // SEO Configuration for current page
  const seoConfig = getPageSEO(location.pathname);

  // Add loading timeout to prevent infinite loading
  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (userLoading) {

        setForceStopLoading(true);
      }
    }, 3000); // 3 second timeout

    return () => clearTimeout(loadingTimeout);
  }, [userLoading]);

  useEffect(() => {
    // Update login state based on user presence
    setIsLoggedIn(!!user);
  }, [user]);

  // Handle potential SPA routing issues
  useEffect(() => {
    // Check if we're on a route that should exist but might be causing issues
    const currentPath = location.pathname;
    const validRoutes = [
      '/', '/dashboard', '/login', '/register', '/profile', '/about', '/contact',
      '/products/all', '/products/user-province', '/user/notifications', '/portefeuille',
      '/payment-topup', '/forgot-password', '/search', '/add-post', '/user/likes', '/users/discover'
    ];
    
    const isValidStaticRoute = validRoutes.includes(currentPath);
    const isValidDynamicRoute = /^\/products\/(view|category)\//.test(currentPath) ||
                                /^\/user\/corner\//.test(currentPath) ||
                                /^\/messages\//.test(currentPath) ||
                                /^\/users\//.test(currentPath) ||
                                /^\/terms-conditions\//.test(currentPath) ||
                                /^\/edit-post\//.test(currentPath);

    // If we're on an invalid route and it's not a known dynamic route, 
    // it might be a server routing issue - redirect to a safe page
    if (!isValidStaticRoute && !isValidDynamicRoute && currentPath !== '/server-down' && currentPath !== '*') {

      // Don't redirect immediately, let React Router handle it naturally
    }
  }, [location.pathname]);

  // Scroll to the top of the page on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    // List of pages that should be accessible even when server is down
    const serverIndependentPages = ['/register', '/login', '/forgot-password', '/terms-conditions', '/about', '/contact'];
    const isServerIndependentPage = serverIndependentPages.some(page => location.pathname.startsWith(page));
    
    if (isServerDown && !location.pathname.includes('/server-down') && !isServerIndependentPage) {

      sessionStorage.setItem('previousRoute', location.pathname);
      navigate('/server-down');
    }
  }, [isServerDown, location.pathname, navigate]);

  // List of pages that should be accessible even when server is down
  const serverIndependentPages = ['/register', '/login', '/forgot-password', '/terms-conditions', '/about', '/contact'];
  const isServerIndependentPage = serverIndependentPages.some(page => location.pathname.startsWith(page));

  if (isServerDown && !location.pathname.includes('/server-down') && !isServerIndependentPage) {
    return null; // Prevent flash of content during redirect
  }

  // Removed isBackendDown check as it's currently unused

  const handleLoginClick = () => {
    redirectToLogin(location.pathname);
  };

  const handleLoginClose = () => {
    setShowLogin(false);
    navigate('/dashboard');
  };
  const handleChangePasswordClose = () => setShowChangePassword(false);

  const handleRegisterSuccess = (userData) => {
    // Set user as logged in after successful registration
    if (userData) {
      setUser(userData);
      setIsLoggedIn(true);
    }
    // Navigate to dashboard directly
    navigate('/dashboard');
  };
  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    setShowLogin(false);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    navigate('/dashboard');
  };

  const handleRegisterClick = () => {
    try {
      navigate('/register');
    } catch (error) {
      console.error('Navigation to register failed:', error);
      // Fallback: try direct window location change
      window.location.href = '/register';
    }
  };

  return (
    <ActivityProvider>
      <div className="App">
        <SEOHead 
          title={seoConfig.title}
          description={seoConfig.description}
          canonical={seoConfig.canonical}
          noIndex={seoConfig.noIndex}
        />
        <SmartAppBanner />
        <Navbar
          onLoginClick={handleLoginClick}
          onRegisterClick={handleRegisterClick}
          onChangePasswordClick={() => setShowChangePassword(true)}
        />
        <div className="main-content">
            <ErrorBoundary>
              <Routes>
                {/* Deep link routes for app detection */}
                <Route path="/produit/:uuid" element={<ProductDeepLink />} />
                <Route path="/produit/:uuid/:slug" element={<ProductDeepLink />} />
                <Route path="/p/:uuid" element={<ProductDeepLink />} />
                <Route path="/product/:uuid" element={<ProductDeepLink />} />
                <Route path="/product/:uuid/:slug" element={<ProductDeepLink />} />
                <Route path="/user/:userUuid" element={<UserDeepLink />} />
                <Route path="/u/:userUuid" element={<UserDeepLink />} />
                <Route path="/profile/:userUuid" element={<UserDeepLink />} />
                <Route path="/waitinglist" element={<WaitingList />} />
                <Route path="/business" element={<WennzeBusiness />} />
                
                <Route path="/" element={<Dashboard />} />
                <Route path="/verify-user/:userId" element={<VerifyUser />} />
                <Route path="/verify-password/:userId" element={<VerifyPassword />} />
                <Route path="/request-password-reset" element={<RequestPasswordReset />} />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile onLoginClick={handleLoginClick} />
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="/users/discover" element={<DiscoverUsers />} />
                <Route path="/users/:userId/:type" element={<UserConnections />} />
                <Route path="/reset-password/:uid/:userId/:token" element={<ResetPassword />} />
                <Route path="/set-password/:userId" element={<SetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/login" element={<Login onClose={handleLoginClose} setIsLoggedIn={setIsLoggedIn} />} />
                <Route path="/register" element={<Register onSuccess={handleRegisterSuccess} />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route path="/products/user/:userId" element={
                  <ProtectedRoute>
                    <ProductsUser />
                  </ProtectedRoute>
                } />
                <Route path="/contact" element={<Contact />} />
                <Route path="/support" element={<Support />} />
                <Route path="/portefeuille" element={
                  <ProtectedRoute>
                    <WalletView />
                  </ProtectedRoute>
                } />
                <Route path="/payment-topup" element={
                  <ProtectedRoute>
                    <PaymentTopup />
                  </ProtectedRoute>
                } />
                <Route path="/communication" element={
                  <ProtectedRoute>
                    <CommunicationPage />
                  </ProtectedRoute>
                } />
                <Route path="/sms-history" element={
                  <ProtectedRoute>
                    <MessageList />
                  </ProtectedRoute>
                } />
                <Route path="/sms-history/:messageId" element={
                  <ProtectedRoute>
                    <MessageDetail />
                  </ProtectedRoute>
                } />
                <Route path="/products/all" element={<ProductsAll />} />
                <Route path="/products/user-province" element={<ProductsProvince />} />
                <Route path="/messages/:userId" element={
                  <ProtectedRoute>
                    <Indox />
                  </ProtectedRoute>
                } />
                <Route path="/about" element={<About />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/user/notifications" element={
                  <ProtectedRoute>
                    <UserNotifications />
                  </ProtectedRoute>
                } />
                <Route path="/messages/chatroom/:conversationId/:productId/:userId" element={
                  <ProtectedRoute>
                    <ChatRoom />
                  </ProtectedRoute>
                } />
                <Route path="/messages/chatroom/product/:productId/:userId/:productOwnerId" element={
                  <ProtectedRoute>
                    <ChatRoom />
                  </ProtectedRoute>
                } />
                <Route path="/promote-product/:productUuid" element={
                  <ProtectedRoute>
                    <PromoteProduct />
                  </ProtectedRoute>
                } />
                <Route path="/boost-product/:productUuid" element={
                  <ProtectedRoute>
                    <BoostProduct />
                  </ProtectedRoute>
                } />
                <Route path="/products/category/:categoryId/:categoryName" element={
                  <ErrorBoundary>
                    <CategoryProductsPage />
                  </ErrorBoundary>
                } />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/add-post" element={
                  <ProtectedRoute>
                    <AddPost />
                  </ProtectedRoute>
                } />
                <Route path="/edit-post/:uuid" element={
                  <ProtectedRoute>
                    <AddPost />
                  </ProtectedRoute>
                } />
                <Route path="/products/view/:uuid" element={<ProductDetails />} />
                <Route path="/user/corner/:userUuid" element={
                  <ProtectedRoute>
                    <UserCorner />
                  </ProtectedRoute>
                } />
                <Route path="/blocked-users" element={
                  <ProtectedRoute>
                    <BlockedUsers />
                  </ProtectedRoute>
                } />
                <Route path="/user-statistics/:userUuid" element={
                  <ProtectedRoute>
                    <React.Suspense fallback={<div className="loading-spinner-page">Chargement...</div>}>
                      <UserCornerStatistics />
                    </React.Suspense>
                  </ProtectedRoute>
                } />
                {/* Legacy support: redirect old /userconnection URLs to new routes */}
                <Route path="/userconnection/:userId" element={<LegacyUserConnectionRedirect />} />
                <Route path="/user/likes/" element={
                  <ProtectedRoute>
                    <Likes />
                  </ProtectedRoute>
                } />
                <Route path="/delete-account" element={
                  <ProtectedRoute>
                    <DeleteAccount />
                  </ProtectedRoute>
                } />
                <Route path="/delete-account-verification" element={
                  <ProtectedRoute>
                    <DeleteAccountVerification />
                  </ProtectedRoute>
                } />
                <Route path="/terms-conditions/:title" element={<TermsConditions />} />
                <Route path="/server-down" element={<ServerDown />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
        </div>
        {showLogin && location.pathname !== '/login' && !isLoggedIn && (
          <Login onClose={handleLoginClose} setIsLoggedIn={setIsLoggedIn} onLoginSuccess={handleLoginSuccess} />
        )}
        {showChangePassword && (
          <ChangePassword onClose={handleChangePasswordClose} />
        )}
        {showConfirmation && (
          <div className="confirmation-popup">
            <div className="confirmation-content">
              <h2>Compte créé avec succès!</h2>
              <p>Un email a été envoyé à votre adresse. Veuillez vérifier votre boîte de réception pour confirmer votre compte.</p>
              <button onClick={handleConfirmationClose} className="confirmation-button">Fermer</button>
            </div>
          </div>
        )}
        <Footer />
      </div>
    </ActivityProvider>
  );
};

const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

if (!clientId) {
  console.error("Google Client ID is missing. Please check your .env file.");
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <UserProvider>
          <CategoryProvider>
            <CityProvider>
              <PageTitleProvider>
                <ReduxProvider>
                  <AppContent />
                </ReduxProvider>
              </PageTitleProvider>
            </CityProvider>
          </CategoryProvider>
        </UserProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;